import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { newsAPI, type NewsCategory } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { RichTextEditor } from '../components/common';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Save,
  AlertCircle,
  Loader2,
  FileText,
  Mail,
} from 'lucide-react';
import { canManageNews } from '../lib/newsAccess';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../lib/api/types/common';
import { toast } from 'sonner';
import EmailSuppliersModal from '../components/news/EmailSuppliersModal';

export default function NewsForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  // Helix users and customer admins may author news — see lib/newsAccess.
  const canManage = canManageNews(user);
  const isHelixUser = Boolean(user?.is_staff || user?.is_superuser || user?.company_type === 'helix');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);


  const [formData, setFormData] = useState<{
    title: string;
    slug: string;
    summary: string;
    content: string;
    category: string;
    visibility: 'all' | 'customer' | 'supplier' | 'helix';
    priority: 'low' | 'normal' | 'high' | 'urgent';
    is_featured: boolean;
    is_pinned: boolean;
    is_published: boolean;
    author_name: string;
    image_url: string;
    external_link: string;
  }>({
    title: '',
    slug: '',
    summary: '',
    content: '',
    category: '',
    visibility: 'all',
    priority: 'normal',
    is_featured: false,
    is_pinned: false,
    is_published: true,
    author_name: '',
    image_url: '',
    external_link: '',
  });

  useEffect(() => {
    if (!canManage) {
      navigate('/news');
      return;
    }

    loadCategories();
    if (isEdit && id) {
      loadArticle(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, isEdit, id]);

  const loadCategories = async () => {
    try {
      const response = await newsAPI.getCategories();
      setCategories(response.data.results || []);
    } catch (err: unknown) {
      console.error('Error loading categories:', err);
    }
  };

  const loadArticle = async (articleId: number) => {
    try {
      setLoading(true);
      const response = await newsAPI.getNewsArticle(articleId);
      const article = response.data;
      setFormData({
        title: article.title,
        slug: article.slug,
        summary: article.summary,
        content: article.content,
        category: article.category?.toString() || '',
        visibility: article.visibility,
        priority: article.priority,
        is_featured: article.is_featured,
        is_pinned: article.is_pinned,
        is_published: article.is_published,
        author_name: article.author_name,
        image_url: article.image_url,
        external_link: article.external_link,
      });
      setError(null);
    } catch (err: unknown) {
      console.error('Error loading article:', err);
      setError('Failed to load article. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title: string) => {
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        is_published: true,
        category: formData.category ? parseInt(formData.category) : null,
      };

      if (isEdit && id) {
        await newsAPI.updateNews(parseInt(id), submitData);
        toast.success('News article published');
      } else {
        await newsAPI.createNews(submitData);
        toast.success('News article published');
      }
      navigate('/news');
    } catch (err: unknown) {
      console.error('Error publishing article:', err);
      setError(getApiErrorMessage(err, 'Failed to publish article. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!formData.title.trim() || !formData.slug.trim() ||
        !formData.summary.trim() || !formData.content.trim()) {
      setError('Please fill in all required fields (title, slug, summary, content) before saving as draft.');
      return;
    }

    setSavingDraft(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        is_published: false,
        category: formData.category ? parseInt(formData.category) : null,
      };

      if (isEdit && id) {
        await newsAPI.updateNews(parseInt(id), submitData);
        toast.success('Article saved as draft');
      } else {
        await newsAPI.createNews(submitData);
        toast.success('Article saved as draft');
      }
      navigate('/news');
    } catch (err: unknown) {
      console.error('Error saving draft:', err);
      setError(getApiErrorMessage(err, 'Failed to save draft. Please try again.'));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleOpenEmailModal = () => {
    if (!isEdit && (!formData.title.trim() || !formData.summary.trim() || !formData.content.trim())) {
      setError('Please fill in Title, Summary and Content before emailing suppliers.');
      return;
    }
    setEmailModalOpen(true);
  };

  if (!canManage) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {isEdit ? 'Edit News Article' : 'Create News Article'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEdit ? 'Update the news article details' : 'Create a new announcement or news article'}
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/news')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Email Suppliers Modal */}
      {isEdit && id ? (
        <EmailSuppliersModal
          newsId={parseInt(id)}
          newsTitle={formData.title}
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
        />
      ) : (
        <EmailSuppliersModal
          articleData={{
            title: formData.title,
            summary: formData.summary,
            content: formData.content,
            external_link: formData.external_link,
          }}
          newsTitle={formData.title}
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
        />
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Article Details</CardTitle>
            <CardDescription>
              Fill in the details for the news article. Fields marked with * are required.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Enter article title"
                required
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="article-slug"
                required
              />
              <p className="text-sm text-muted-foreground">
                URL-friendly version of the title (auto-generated)
              </p>
            </div>

            {/* Summary */}
            <div className="space-y-2">
              <Label htmlFor="summary">Summary *</Label>
              <Textarea
                id="summary"
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Brief summary (max 200 characters)"
                maxLength={200}
                rows={3}
                required
              />
              <p className="text-sm text-muted-foreground">
                {formData.summary.length}/200 characters
              </p>
            </div>

            {/* Content with Rich Text Editor */}
            <div className="space-y-2">
              <Label>Content *</Label>
              <RichTextEditor
                key={isEdit ? (loading ? 'loading' : 'loaded') : 'create'}
                value={formData.content}
                onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
                placeholder="Full article content..."
              />
              <p className="text-sm text-muted-foreground">
                Use the toolbar to format text with bold, italic, headings, lists, and links.
              </p>
            </div>

            {/* Category & Priority Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category || "none"} onValueChange={(value) => setFormData({ ...formData, category: value === "none" ? "" : value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority *</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value as 'low' | 'normal' | 'high' | 'urgent' })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Visibility */}
            <div className="space-y-2">
              <Label htmlFor="visibility">Visibility *</Label>
              <Select value={formData.visibility} onValueChange={(value) => setFormData({ ...formData, visibility: value as 'all' | 'customer' | 'supplier' | 'helix' })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="customer">Customers Only</SelectItem>
                  <SelectItem value="supplier">Suppliers Only</SelectItem>
                  {/* Helix-internal articles are hidden from customer admins by
                      the backend queryset, so offering them the option would
                      make their own article vanish and become uneditable. */}
                  {isHelixUser && <SelectItem value="helix">Helix Admin Only</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Author Name Override */}
            <div className="space-y-2">
              <Label htmlFor="author_name">Author Name (Optional)</Label>
              <Input
                id="author_name"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                placeholder="Override author display name"
              />
              <p className="text-sm text-muted-foreground">
                Leave blank to use your name
              </p>
            </div>

            {/* Image URL */}
            <div className="space-y-2">
              <Label htmlFor="image_url">Featured Image URL (Optional)</Label>
              <Input
                id="image_url"
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            {/* External Link */}
            <div className="space-y-2">
              <Label htmlFor="external_link">External Link (Optional)</Label>
              <Input
                id="external_link"
                type="url"
                value={formData.external_link}
                onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
                placeholder="https://example.com/documentation"
              />
              <p className="text-sm text-muted-foreground">
                Link to related documentation or resources
              </p>
            </div>

            {/* Toggles */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_featured">Featured</Label>
                  <p className="text-sm text-muted-foreground">
                    Mark as a featured article
                  </p>
                </div>
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="is_pinned">Pinned</Label>
                  <p className="text-sm text-muted-foreground">
                    Pin this article to the top
                  </p>
                </div>
                <Switch
                  id="is_pinned"
                  checked={formData.is_pinned}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_pinned: checked })}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3 pt-4 flex-wrap">
              <Button type="submit" disabled={saving || savingDraft}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Publish
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saving || savingDraft}
              >
                {savingDraft ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving Draft...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Save as Draft
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleOpenEmailModal}
                disabled={saving || savingDraft}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Selected Suppliers
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate('/news')}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
